import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../firebase';

function PinLogin({ onSuccess }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [mode, setMode] = useState('pin'); // pin | forgot-pin | new-pin | confirm-pin
  const [password, setPassword] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (mode === 'pin') {
        if (e.key >= '0' && e.key <= '9') handleKey(e.key);
        if (e.key === 'Backspace') handleDel();
      }
      if (mode === 'new-pin' || mode === 'confirm-pin') {
        if (e.key >= '0' && e.key <= '9') handleNewPinKey(e.key);
        if (e.key === 'Backspace') handleNewPinDel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const handleKey = (n) => {
    if (pin.length >= 8) return;
    const newP = pin + n;
    setPin(newP);
    if (newP.length === 8) {
      setTimeout(async () => {
        try {
          const snap = await getDoc(doc(db, 'users', auth.currentUser.uid));
          if (snap.exists() && snap.data().pin === newP) {
            onSuccess();
          } else {
            setError('Incorrect PIN — try again');
            setPin('');
          }
        } catch (e) {
          setError('Error verifying PIN');
          setPin('');
        }
      }, 200);
    }
  };

  const handleDel = () => {
    setPin(pin.slice(0, -1));
    setError('');
  };

  const handleNewPinKey = (n) => {
    if (mode === 'new-pin') {
      if (newPin.length >= 8) return;
      const p = newPin + n;
      setNewPin(p);
      if (p.length === 8) setTimeout(() => setMode('confirm-pin'), 300);
    } else {
      if (confirmPin.length >= 8) return;
      const p = confirmPin + n;
      setConfirmPin(p);
      if (p.length === 8) {
        setTimeout(async () => {
          if (p === newPin) {
            try {
              await setDoc(doc(db, 'users', auth.currentUser.uid), { pin: newPin }, { merge: true });
              setMode('pin');
              setNewPin('');
              setConfirmPin('');
              setError('');
            } catch (e) {
              setError('Error saving PIN');
            }
          } else {
            setError('PINs do not match');
            setConfirmPin('');
          }
        }, 200);
      }
    }
  };

  const handleNewPinDel = () => {
    if (mode === 'new-pin') setNewPin(newPin.slice(0, -1));
    else setConfirmPin(confirmPin.slice(0, -1));
    setError('');
  };

  const verifyPassword = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, auth.currentUser.email, password);
      setMode('new-pin');
      setPassword('');
    } catch (e) {
      setError('Incorrect password');
    }
    setLoading(false);
  };

  const Dots = ({ value }) => (
    <div className="flex justify-center gap-2 mb-2">
      {[...Array(8)].map((_, i) => (
        <div key={i} className={`w-3 h-3 rounded-full border-2 transition-all ${
          i < value.length ? 'bg-yellow-400 border-yellow-400' : 'border-gray-600'
        }`} />
      ))}
    </div>
  );

  const KeyPad = ({ onKey, onDel }) => (
    <div className="grid grid-cols-3 gap-2 mt-3">
      {[1,2,3,4,5,6,7,8,9].map(n => (
        <button key={n} onClick={() => onKey(String(n))}
          className="py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-medium hover:bg-gray-700 active:scale-95 transition-all">
          {n}
        </button>
      ))}
      <div></div>
      <button onClick={() => onKey('0')}
        className="py-3 bg-gray-800 border border-gray-700 rounded-lg text-white font-medium hover:bg-gray-700 active:scale-95 transition-all">
        0
      </button>
      <button onClick={onDel}
        className="py-3 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:bg-gray-700 active:scale-95 transition-all text-sm">
        ⌫
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-5">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-7 w-72 text-center">

        <svg width="40" height="40" viewBox="0 0 100 130" fill="none" className="mx-auto mb-4">
          <defs>
            <linearGradient id="plg" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f2c832"/>
              <stop offset="100%" stopColor="#e8496a"/>
            </linearGradient>
          </defs>
          <path d="M58 4L84 4L84 10L68 10L68 54L84 54L84 62L52 126L42 126L62 72L44 72L44 62L16 62L16 54L32 54L32 10L16 10L16 4L42 4L42 54L58 54Z" fill="url(#plg)"/>
        </svg>

        {/* PIN entry */}
        {mode === 'pin' && (
          <>
            <h2 className="text-lg font-bold text-white mb-1">Enter PIN</h2>
            <p className="text-xs text-gray-500 mb-4">Click or use keyboard</p>
            <Dots value={pin} />
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
            <KeyPad onKey={handleKey} onDel={handleDel} />
            <p className="text-xs text-gray-600 mt-4">
              <span onClick={() => { setMode('forgot-pin'); setError(''); }}
                className="text-yellow-400 cursor-pointer hover:underline">
                Forgot PIN?
              </span>
            </p>
          </>
        )}

        {/* Forgot PIN — verify password */}
        {mode === 'forgot-pin' && (
          <>
            <h2 className="text-lg font-bold text-white mb-1">Reset PIN</h2>
            <p className="text-xs text-gray-500 mb-5">Enter your password to verify</p>
            <div className="mb-4 text-left">
              <label className="text-xs text-gray-500 uppercase tracking-wide mb-1 block">Password</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Your password"
                className="w-full bg-gray-950 border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-yellow-500 transition-all placeholder-gray-700"
              />
            </div>
            {error && <div className="bg-red-950 border border-red-800 rounded-lg px-3 py-2 text-xs text-red-400 mb-4">{error}</div>}
            <button
              onClick={verifyPassword}
              disabled={loading || !password}
              className="w-full bg-gradient-to-r from-yellow-500 to-pink-600 text-white font-semibold py-2.5 rounded-lg text-sm hover:opacity-90 transition-all disabled:opacity-40 mb-3">
              {loading ? 'Verifying...' : 'Verify & Reset PIN'}
            </button>
            <p className="text-xs">
              <span onClick={() => { setMode('pin'); setError(''); }}
                className="text-gray-500 cursor-pointer hover:text-yellow-400">
                ← Back
              </span>
            </p>
          </>
        )}

        {/* New PIN */}
        {mode === 'new-pin' && (
          <>
            <h2 className="text-lg font-bold text-white mb-1">New PIN</h2>
            <p className="text-xs text-gray-500 mb-4">Enter your new 8-digit PIN</p>
            <Dots value={newPin} />
            <p className="text-xs text-gray-600 mb-2">{newPin.length} / 8</p>
            <KeyPad onKey={handleNewPinKey} onDel={handleNewPinDel} />
          </>
        )}

        {/* Confirm new PIN */}
        {mode === 'confirm-pin' && (
          <>
            <h2 className="text-lg font-bold text-white mb-1">Confirm PIN</h2>
            <p className="text-xs text-gray-500 mb-4">Enter your new PIN again</p>
            <Dots value={confirmPin} />
            {error && <p className="text-xs text-red-400 mb-2">{error}</p>}
            <KeyPad onKey={handleNewPinKey} onDel={handleNewPinDel} />
          </>
        )}

      </div>
    </div>
  );
}

export default PinLogin;